import React, { useState } from "react";
import Modal from "react-modal";
import CloseButton from "../../atoms/buttons/CloseButton";
import { modalStyles } from "../../../assets/styles/modalStyles";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';
import { FaUserCircle } from 'react-icons/fa';
import ReactDropdown from "react-dropdown";
import { FaPowerOff, FaCheckCircle, FaCalendarAlt, FaPercent } from "react-icons/fa";
import { VscAccount } from "react-icons/vsc";
import { RiAccountCircleFill } from "react-icons/ri";
import { getDetailedDoctorAttendanceAnalytics, DetailedDoctorAttendanceResponse, downloadDoctorAttendanceData } from '../../../lib/apis/analytics';
import ProfilePicture from '../../atoms/ProfilePicture';
import * as XLSX from "xlsx";
import { useHospDocData } from "../../../lib/contexts/HospitalDoctorContext";

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const dummyStats = [
  {
    label: "Turned Off Sessions",
    value: 2,
    color: "from-blue-400 to-sky-500",
    hover: "hover:from-blue-600 hover:to-sky-700",
  },
  {
    label: "Attended Sessions",
    value: 8,
    color: "from-teal-400 to-green-400",
    hover: "hover:from-teal-600 hover:to-green-700",
  },
  {
    label: "Scheduled Sessions",
    value: 10,
    color: "from-yellow-400 to-orange-400",
    hover: "hover:from-yellow-600 hover:to-orange-700",
  },
  {
    label: "Attendance %",
    value: "80%",
    color: "from-purple-400 to-pink-400",
    hover: "hover:from-purple-600 hover:to-pink-700",
  },
];

const dummyRows = [
  { id: 1, date: "2024-07-01", session: "Morning", checkin: "09:05", delay: "5 min", status: "On Time" },
  { id: 2, date: "2024-07-02", session: "Evening", checkin: "18:10", delay: "10 min", status: "Late" },
  { id: 3, date: "2024-07-03", session: "Morning", checkin: "09:00", delay: "0 min", status: "On Time" },
];

const columns: GridColDef[] = [
  { field: 'date', headerName: 'Date', flex: 1, minWidth: 120 },
  { field: 'session', headerName: 'Session', flex: 1, minWidth: 120 },
  { field: 'checkin', headerName: 'Check-in Time', flex: 1, minWidth: 120 },
  { field: 'delay', headerName: 'Delay', flex: 1, minWidth: 100 },
  { field: 'status', headerName: 'Status', flex: 1, minWidth: 120 },
];

const customModalStyles = {
  ...modalStyles,
  content: {
    ...modalStyles.content,
    width: '100vw',
    maxWidth: '900px',
    minWidth: '320px',
    // Add max height for modal
   // Enable scroll if needed
    
  },
};

const DetailDoctorAttendanceModal = ({ doctor, onClose }: { doctor: any; onClose: () => void }) => {
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState<DetailedDoctorAttendanceResponse | null>(null);
  const { hospitalID} =
  useHospDocData();

  // Year dropdown options (current year and previous 4 years)
  const currentYear = today.getFullYear();
  const minYear = 2024;
  const yearOptions = Array.from({ length: currentYear - minYear + 1 }, (_, i) => (currentYear - i).toString());
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Calculate start and end date for the selected month
  const year = selectedYear;
  const lastDay = new Date(year, selectedMonth + 1, 0).getDate();
  const start_date = `${year}-${String(selectedMonth + 1).padStart(2, '0')}-01`;
  const end_date = `${year}-${String(selectedMonth + 1).padStart(2, '0')}-${lastDay}`;

  console.log('DetailDoctorAttendanceModal rendered');
  React.useEffect(() => {
    console.log('doctor:', doctor);
    console.log('doctor.mapping_id:', doctor?.mapping_id);
    console.log('DetailDoctorAttendanceModal useEffect triggered');
    if (!(doctor?.hospital_id || doctor?.doctor_id) || !(doctor?.doctor_mapping_id || doctor?.mapping_id)) return;
    const params = {
      hospital_id: hospitalID||"",
      doctor_mapping_id: doctor.mapping_id,
      start_date,
      end_date,
    };
    console.log('Doctor Attendance API Params:', params);
    setLoading(true);
    getDetailedDoctorAttendanceAnalytics(params)
      .then((data) => {
        setAttendanceData(data);
        console.log('Doctor Attendance API Data:', data);
      })
      .catch((err) => {
        console.error('Doctor Attendance API Error:', err);
      })
      .finally(() => setLoading(false));
  }, [doctor, selectedMonth, selectedYear, hospitalID]);

  // Prepare stats from API data
  const stats = attendanceData ? [
    {
      label: "Total Sessions",
      value: attendanceData.aggregated_data.total_sessions_scheduled,
      color: "from-blue-400 to-sky-500",
      hover: "hover:from-blue-600 hover:to-sky-700",
    },
    {
      label: "Attended Sessions",
      value: attendanceData.aggregated_data.attended_sessions,
      color: "from-teal-400 to-green-400",
      hover: "hover:from-teal-600 hover:to-green-700",
    },
    {
      label: "Unattended Sessions",
      value: attendanceData.aggregated_data.unattended_sessions ?? attendanceData.aggregated_data.total_sessions_absent,
      color: "from-yellow-400 to-orange-400",
      hover: "hover:from-yellow-600 hover:to-orange-700",
    },
    {
      label: "Disabled Sessions",
      value: attendanceData.aggregated_data.disabled_sessions,
      color: "from-purple-400 to-pink-400",
      hover: "hover:from-purple-600 hover:to-pink-700",
    },
  ] : [];

  // Prepare table rows from API data
  const rows = attendanceData ? attendanceData.individual_sessions.map((session, idx) => ({
    id: idx + 1,
    date: session.date,
    session: session.session_timings,
    checkin: session.doctor_checked_in_time,
    delay: `${session.delay_in_minutes} min`,
    status: session.status,
  })) : [];

  // Doctor details from API if available
  const doctorDetails = attendanceData?.doctor_details || doctor;



  const handleDownloadExcel = async () => {
    const mappingId = doctor?.mapping_id || doctor?.doctor_mapping_id;
    if (!mappingId) {
      alert("Doctor mapping ID not found");
      return;
    }
  
    setLoading(true);
  
    const params = {
      hospital_id: hospitalID || "",
      doctor_mapping_id: mappingId,
      start_date,
      end_date,
    };
  
    try {
      // Fetch the attendance data from the same API
      const data = await getDetailedDoctorAttendanceAnalytics(params);

      if (!data?.individual_sessions?.length) {
        alert("No attendance data available to export.");
        setLoading(false);
        return;
      }

      // Convert sessions to Excel rows
      const worksheet = XLSX.utils.json_to_sheet(
        data.individual_sessions.map((session) => ({
          Date: session.date,
          "Session Timings": session.session_timings,
          "Checked-In Time": session.doctor_checked_in_time,
          "Delay (Minutes)": session.delay_in_minutes,
          Status: session.status,
        }))
      );

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");

      const fileName = `Doctor_Attendance_${months[selectedMonth]}_${year}.xlsx`;
      XLSX.writeFile(workbook, fileName);

    } catch (err) {
      console.error("Export API Error:", err);
      alert("Failed to export doctor attendance data.");
    } finally {
      setLoading(false);
    }
  };
  
  if (!doctor) return null;
  return (
    <Modal
      isOpen={true}
      onRequestClose={onClose}
      style={customModalStyles}
      ariaHideApp={false}
     
    >
      {/* Header */}
      <div className="px-6 py-3 flex flex-row items-center justify-between border-b-[0.5px] border-sbBorder">
        <p className="text-dark text-md font-semibold">Doctor Attendance Details</p>
        <CloseButton handleClick={onClose} />
      </div>
     <div className="max-h-[90vh] overflow-y-auto side_bar">
       {/* Doctor Card and Month/Year Dropdowns in a row, centered */}
       <div className="flex flex-col md:flex-row items-start justify-between w-full max-w-5xl bg-white mx-auto px-2 md:px-6 pt-4 md:pt-6 pb-2 gap-4">
        <div className="flex flex-col md:flex-row items-center justify-between w-full bg-gradient-to-r from-blue-50 to-blue-200 border border-gray-200 rounded-xl px-3 md:px-6 py-3 shadow-sm min-w-[220px] md:min-w-[420px] ">
          <div className="flex flex-row items-center gap-6">
            <div className="flex items-center justify-center w-28 h-28 rounded-full bg-gray-200 overflow-hidden relative">
              {doctorDetails?.profile_picture ? (
                <img src={doctorDetails.profile_picture} alt="Doctor" className="w-full h-full object-cover rounded-full" />
              ) : (
                <div className="flex items-center justify-center w-full h-full bg-gradient-to-br from-blue-400 to-indigo-500 shadow-lg ">
                  <RiAccountCircleFill className="w-20 h-20 text-white" />
                </div>
              )}
            </div>
            <div className="flex flex-col justify-center">
              <span className="text-xl font-extrabold text-gray-800 leading-tight">
                {doctorDetails?.name ? `Dr. ${doctorDetails.name}` : doctorDetails?.full_name ? `Dr. ${doctorDetails.full_name}` : '-'}
              </span>
              {/* Specialization and Qualification below name, normal text */}
              {(doctorDetails?.qualification || doctorDetails?.specialization) && (
                <span className="text-base text-gray-700 mt-1">
                  {[doctorDetails?.qualification, doctorDetails?.specialization].filter(Boolean).join(' | ')}
                </span>
              )}
              {/* Contact number */}
              {doctorDetails?.contact_number && (
                <span className="text-sm text-gray-600 mt-1">Contact: {doctorDetails.contact_number}</span>
              )}
              {/* Doctor Attendance below specialization */}
              <span className="mt-2 font-bold  text-purple-600 px-2 py-1 text-md rounded">
                Doctor Attendance: <span className="text-purple-800">{(attendanceData && selectedMonth !== today.getMonth()) ? ((attendanceData.aggregated_data.attended_sessions / (attendanceData.aggregated_data.total_sessions_scheduled || 1)) * 100).toFixed(2) : '--'}%</span>
              </span>
            </div>
          </div>
          {/* Month and Year Dropdowns on the right side of the card */}
          <div className="mt-4 md:mt-0 md:ml-8 flex flex-col md:flex-row gap-2 md:gap-4 items-center w-full md:w-auto">
            <ReactDropdown
              options={months}
              value={months[selectedMonth]}
              onChange={option => setSelectedMonth(months.indexOf(option.value))}
              controlClassName="!rounded-lg px-3 py-2 border border-sbBorder bg-white w-full md:w-[120px]"
              menuClassName="overflow-x-hidden side_bar roundd-lg !mt-1"
              placeholder="Select Month"
            />
            <ReactDropdown
              options={yearOptions}
              value={selectedYear.toString()}
              onChange={option => setSelectedYear(Number(option.value))}
              controlClassName="!rounded-lg px-3 py-2 border border-sbBorder bg-white w-full md:w-[100px]"
              menuClassName="overflow-x-hidden side_bar roundd-lg !mt-1 max-h-40 overflow-y-auto"
              placeholder="Select Year"
            />
          </div>
        </div>
      </div>
      {/* Body: Only Attendance Details, centered */}
      <div className="flex flex-col items-center justify-center px-6 py-5 side_bar">
        <div className="w-full max-w-5xl flex flex-col">
          {/* Stats Cards with animation and color theme */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 backdrop-blur-md">
            {loading ? (
              <div className="col-span-4 flex justify-center items-center h-32">Loading...</div>
            ) : (
              stats.length > 0 ? (
                stats.map((stat, idx) => (
                  <div
                    key={stat.label}
                    className={`p-4 rounded-lg cursor-pointer flex flex-col justify-between items-start h-full transition-all duration-300 hover:shadow-xl bg-gradient-to-br ${stat.color} text-white ${stat.hover} transform hover:scale-105`}
                    style={{ minHeight: 110 }}
                  >
                    <h3 className="text-base font-medium text-white mb-2">{stat.label}</h3>
                    <span className="text-4xl font-bold text-white">{stat.value}</span>
                  </div>
                ))
              ) : (
                <div className="col-span-4 flex justify-center items-center h-32 text-gray-500">No attendance data for this month.</div>
              )
            )}
          </div>
          {/* Export Button */}
          <div className="flex justify-end mb-2">
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadExcel}
            >
              Export
            </Button>
          </div>
          {/* DataGrid Table */}
          <div style={{ height: 230, width: '100%' }} className="side_bar">
            <DataGrid
              rows={rows}
              columns={columns}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              autoHeight
              disableRowSelectionOnClick
              loading={loading}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              slots={{
                noRowsOverlay: () => (
                  <div className="flex items-center justify-center h-full text-gray-500">No attendance records found for this month.</div>
                ),
              }}
            />
          </div>
        </div>
      </div>
     </div>
      {/* Footer removed as per request */}
    </Modal>
  );
};

export default DetailDoctorAttendanceModal;
